<template>
  <div>
    <b-row class="mb-5" align-v="center">
      <b-col>
        <h4>Documentos e Atas</h4>
      </b-col>
      <b-col cols="auto" v-if="canManage && documents.length > 0">
        <b-button class="btn medium blue" @click="openCreateDocumentModal">
          Subir arquivo
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <div v-if="loading" class="text-center">
          <b-spinner variant="success" label="Carregando..."></b-spinner>
        </div>
        <div class="student-documents-list" v-else-if="documents.length > 0">
          <StudentDocumentItem
            v-for="studentDocument in documents"
            :key="studentDocument.id"
            :studentDocument="studentDocument"
            :typeLabel="documentTypeLabel(studentDocument.type)"
            :isMine="isCurrentUserDocument(studentDocument)"
            :canDelete="canManage && canDeleteDocument(studentDocument)"
            @delete="deleteDocument"
          />
        </div>
        <div class="message-not-found" v-else>
          <p>Ainda não existem documentos ou atas para esse aluno.</p>
          <b-button
            v-if="canManage"
            class="btn medium blue mt-3"
            @click="openCreateDocumentModal"
          >
            Subir arquivo
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-modal id="modal-create-student-document" hide-footer>
      <template #modal-title>Subir arquivo</template>
      <div class="d-block text-center">
        <b-form>
          <b-form-group class="document-form-group">
            <template #label>
              Tipo do arquivo <span class="required-mark">*</span>
            </template>
            <b-form-radio-group
              v-model="documentForm.type"
              :options="documentTypeOptions"
            ></b-form-radio-group>
          </b-form-group>
          <b-form-group
            class="document-form-group mt-4"
            label="Comentário"
            label-for="form-student-document"
          >
            <b-form-textarea
              id="form-student-document"
              v-model="documentForm.comment"
              placeholder="Escreva aqui um comentário (opcional)"
              rows="4"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <UploadMultipleFiles
              label="Arquivo *"
              v-model="documentForm.files"
              allow-multiple
              :accept="acceptedExtensions.join(', ')"
              hint="Apenas arquivos PDF ou DOCX com tamanho máximo de 5MB."
            />
          </b-form-group>
          <b-form-group class="wrap-btn-form">
            <b-button
              class="btn small blue"
              @click="createDocument"
              :disabled="submitting"
            >
              {{ submitting ? "Enviando..." : "Enviar" }}
            </b-button>
          </b-form-group>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import StudentDocumentItem from "@/components/StudentDocumentItem.vue";
import UploadMultipleFiles from "@/components/inputs/UploadMultipleFiles.vue";
import StudentsService from "@/services/students.service";
import store from "../store";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Signed links returned by the API expire in 30 minutes.
const REFRESH_INTERVAL = 25 * 60 * 1000;

export default {
  name: "StudentDocumentsComponent",
  components: {
    StudentDocumentItem,
    UploadMultipleFiles,
  },
  props: {
    studentId: {
      type: Number,
      required: true,
    },
    canManage: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const authData = store.getters["auth/getAuthData"];

    return {
      currentUserId: authData.id,
      currentUserRoles: authData.role || [],
      documents: [],
      loading: false,
      submitting: false,
      refreshTimer: null,
      acceptedExtensions: [".pdf", ".docx"],
      documentTypeOptions: [
        { value: "document", text: "Documento do aluno" },
        { value: "minute", text: "Ata de reunião" },
      ],
      documentForm: {
        type: "document",
        comment: null,
        files: [],
      },
    };
  },
  methods: {
    documentTypeLabel(type) {
      const option = this.documentTypeOptions.find(
        (documentType) => documentType.value === type
      );
      return option ? option.text : type;
    },

    async getDocuments() {
      if (!this.studentId) {
        return;
      }

      this.loading = this.documents.length === 0;
      try {
        const response = await StudentsService.getStudentDocuments(
          this.studentId
        );
        this.documents = response.data.result || [];
      } catch (err) {
        this.documents = [];
      }
      this.loading = false;
    },

    openCreateDocumentModal() {
      this.documentForm = {
        type: "document",
        comment: null,
        files: [],
      };
      this.$bvModal.show("modal-create-student-document");
    },

    showAlert(text) {
      this.$swal.fire({
        title: "Alerta",
        text,
        icon: "error",
      });
    },

    validateForm() {
      const { type, files } = this.documentForm;

      if (!type) {
        return "Selecione o tipo do arquivo: Documento do aluno ou Ata de reunião.";
      }
      if (!files || files.length === 0) {
        return "Selecione um arquivo para enviar.";
      }

      for (const file of files) {
        const fileName = file.name.toLowerCase();
        const hasAcceptedExtension = this.acceptedExtensions.some(
          (extension) => fileName.endsWith(extension)
        );
        if (!hasAcceptedExtension) {
          return `Formato não permitido em "${file.name}". Envie arquivos PDF ou DOCX.`;
        }
        if (file.size > MAX_FILE_SIZE) {
          return `O arquivo "${file.name}" ultrapassa o limite de 5 MB.`;
        }
      }

      return null;
    },

    errorMessage(err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || "";
      const error = err?.response?.data?.error || "";

      if (status === 401) {
        return "Você não tem permissão para acessar os documentos deste aluno.";
      }
      if (status === 403) {
        return "Apenas o autor do envio, a coordenação ou a direção podem excluir este arquivo.";
      }
      if (status === 400) {
        if (message === "File is required") {
          return "Selecione um arquivo para enviar.";
        }
        if (message.startsWith("Type must be one of")) {
          return "Selecione o tipo do arquivo: Documento do aluno ou Ata de reunião.";
        }
        if (message === "Student not found") {
          return "Aluno não encontrado.";
        }
        if (message === "Student document not found") {
          return "Arquivo não encontrado. Ele pode já ter sido excluído.";
        }
      }
      if (status === 422) {
        if (error.includes("tamanho")) {
          return "O arquivo ultrapassa o limite de 5 MB.";
        }
        return "Formato não permitido. Envie um arquivo PDF ou DOCX.";
      }

      return "Ocorreu um erro inesperado. Tente novamente.";
    },

    async createDocument() {
      const validationError = this.validateForm();
      if (validationError) {
        this.showAlert(validationError);
        return;
      }

      this.submitting = true;
      try {
        const formData = new FormData();
        formData.append("type", this.documentForm.type);
        formData.append("comment", this.documentForm.comment || "");
        this.documentForm.files.forEach((file) =>
          formData.append("files", file)
        );

        await StudentsService.createStudentDocument(this.studentId, formData);

        this.$swal.fire({
          title: "Sucesso",
          text: "Arquivo enviado com sucesso.",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });

        this.$bvModal.hide("modal-create-student-document");
        await this.getDocuments();
      } catch (err) {
        this.showAlert(this.errorMessage(err));
      }
      this.submitting = false;
    },

    deleteDocument(documentId) {
      this.$swal
        .fire({
          title: "Tem certeza?",
          text: "Você não poderá reverter isso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#68c5b7",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sim, excluir!",
          cancelButtonText: "Cancelar",
        })
        .then(async (result) => {
          if (!result.isConfirmed) {
            return;
          }

          try {
            await StudentsService.deleteStudentDocument(
              this.studentId,
              documentId
            );
            this.$swal.fire({
              title: "Excluído!",
              text: "Arquivo excluído com sucesso.",
              icon: "success",
              showConfirmButton: false,
              timer: 1500,
            });
          } catch (err) {
            this.$swal.fire({
              title: "Erro!",
              text: this.errorMessage(err),
              icon: "error",
            });
          }
          await this.getDocuments();
        });
    },

    isCurrentUserDocument(studentDocument) {
      return studentDocument.user_id === this.currentUserId;
    },

    canDeleteDocument(studentDocument) {
      return (
        this.isCurrentUserDocument(studentDocument) ||
        this.currentUserRoles.includes("coordinator") ||
        this.currentUserRoles.includes("admin")
      );
    },
  },
  async mounted() {
    await this.getDocuments();
    this.refreshTimer = setInterval(this.getDocuments, REFRESH_INTERVAL);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
  },
  watch: {
    studentId: {
      immediate: false,
      async handler(newVal) {
        if (newVal) {
          this.documents = [];
          await this.getDocuments();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.student-documents-list {
  display: flex;
  flex-direction: column;
}

.required-mark {
  color: $red;
}

.document-form-group {
  ::v-deep legend,
  ::v-deep > label {
    font-weight: $bold;
  }

  ::v-deep .custom-control-label {
    font-weight: $regular;
  }
}

.message-not-found {
  width: 100%;
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  box-sizing: border-box;
  margin-bottom: 20px;

  p {
    margin: 0;
    color: #666666;
    font-size: 16px;
  }

  .btn {
    margin: 0 auto;
  }
}
</style>
